import { forwardRef, useId } from 'react';
import { Search, X } from 'lucide-react';
import { cx } from './cx';
import styles from './Field.module.css';

/** Beschriftung, Hinweis und Fehlermeldung rund um ein Eingabeelement */
export function Field({ id, label, hint, error, required, className, children }) {
  return (
    <div className={cx(styles.field, className)}>
      {label && (
        <label className={styles.label} htmlFor={id}>
          {label}{required && <span className={styles.required} aria-hidden> *</span>}
        </label>
      )}
      {children}
      {error ? (
        <p id={`${id}-msg`} className={styles.error} role="alert">{error}</p>
      ) : hint ? (
        <p id={`${id}-msg`} className={styles.hint}>{hint}</p>
      ) : null}
    </div>
  );
}

function useFieldProps({ id, label, hint, error, required, className }) {
  const autoId = useId();
  const fieldId = id || autoId;
  return {
    fieldId,
    fieldProps: { id: fieldId, label, hint, error, required, className },
    controlProps: {
      id: fieldId,
      required,
      'aria-invalid': error ? true : undefined,
      'aria-describedby': error || hint ? `${fieldId}-msg` : undefined
    }
  };
}

export const Input = forwardRef(function Input({ id, label, hint, error, required, className, ...props }, ref) {
  const { fieldProps, controlProps } = useFieldProps({ id, label, hint, error, required, className });
  return (
    <Field {...fieldProps}>
      <input ref={ref} className={styles.control} {...controlProps} {...props} />
    </Field>
  );
});

export const Textarea = forwardRef(function Textarea({ id, label, hint, error, required, className, rows = 4, ...props }, ref) {
  const { fieldProps, controlProps } = useFieldProps({ id, label, hint, error, required, className });
  return (
    <Field {...fieldProps}>
      <textarea ref={ref} rows={rows} className={cx(styles.control, styles.textarea)} {...controlProps} {...props} />
    </Field>
  );
});

export const Select = forwardRef(function Select({ id, label, hint, error, required, className, children, ...props }, ref) {
  const { fieldProps, controlProps } = useFieldProps({ id, label, hint, error, required, className });
  return (
    <Field {...fieldProps}>
      <select ref={ref} className={cx(styles.control, styles.select)} {...controlProps} {...props}>
        {children}
      </select>
    </Field>
  );
});

/** Suchfeld mit Lupe und Löschen-Knopf; onChange liefert direkt den Text */
export const SearchField = forwardRef(function SearchField(
  { value, onChange, placeholder = 'Suchen …', label = 'Suche', className, ...props },
  ref
) {
  return (
    <div className={cx(styles.search, className)}>
      <Search className={styles.searchIcon} aria-hidden />
      <input
        ref={ref}
        type="search"
        enterKeyHint="search"
        aria-label={label}
        className={cx(styles.control, styles.searchInput)}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        {...props}
      />
      {value && (
        <button type="button" className={styles.clear} aria-label="Suche leeren" onClick={() => onChange('')}>
          <X aria-hidden />
        </button>
      )}
    </div>
  );
});
